<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import DayChart from '@/components/dashboards/DayChart.vue'

const props = defineProps({
  report: { type: Object, required: true },
  routedShare: { type: Number, default: null },
})

const { t } = useI18n()

// Validated for colour-blind readers and for contrast on the light surface
const COLOR = { blue: '#0588f0', teal: '#008573', amber: '#ab6400', violet: '#654dc4', ruby: '#e54666' }

const totals = computed(() => props.report.totals)
const days = computed(() => props.report.days)
const avgPoints = computed(() => days.value.map(d => ({ day: d.day, value: d.ratings_avg })))
const countPoints = computed(() => days.value.map(d => ({ day: d.day, value: d.ratings_count })))
const funnel = computed(() => [
  { key: 'FUNNEL_ROUTED', value: totals.value.routed_to_bot, color: COLOR.blue },
  { key: 'FUNNEL_CLOSED', value: totals.value.bot_closed, color: COLOR.teal },
  { key: 'FUNNEL_HANDED', value: totals.value.bot_handed_over, color: COLOR.amber },
  { key: 'FUNNEL_PLEA', value: totals.value.bot_escalated_by_client, color: COLOR.violet },
])
const histogram = computed(() =>
  [1, 2, 3, 4, 5].map(mark => ({ mark, count: totals.value.ratings_hist?.[mark] ?? 0 }))
)
const histMax = computed(() => Math.max(1, ...histogram.value.map(h => h.count)))
const lowMarks = computed(() => histogram.value.filter(h => h.mark <= 3).reduce((s, h) => s + h.count, 0))
const reasons = computed(() => Object.entries(props.report.handover_reasons || {}))
const reasonsTotal = computed(() => Math.max(1, totals.value.bot_handed_over))
const pct = (part, whole) => (whole ? Math.round((part * 100) / whole) : 0)
const fmt = n => (n == null ? '—' : new Intl.NumberFormat('ru-RU').format(n))
const fmtAvg = v => (v == null ? '—' : v.toFixed(1).replace('.', ','))
// CSI thresholds: 80 and up is good, under 60 is a problem
const csiTone = v => {
  if (v == null) return 'text-n-slate-11'
  if (v >= 80) return 'text-n-teal-11'
  return v >= 60 ? 'text-n-slate-12' : 'text-n-ruby-11'
}
const fmtSec = s => (s == null ? '—' : `${s} ${t('MONITORING.SECONDS')}`)
const markColor = mark => (mark >= 4 ? COLOR.blue : mark === 3 ? COLOR.amber : COLOR.ruby)
</script>

<template>
  <div class="flex flex-col gap-6">
    <div class="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5">
      <div class="flex flex-col gap-1 p-4 rounded-xl border border-n-weak bg-n-solid-1">
        <span class="text-xs text-n-slate-11">{{ $t('DASHBOARDS.BOT.ROUTED') }}</span>
        <span class="text-2xl font-semibold text-n-slate-12">{{ fmt(totals.routed_to_bot) }}</span>
        <span v-if="routedShare != null" class="text-xs text-n-slate-11">
          {{ $t('DASHBOARDS.BOT.ROUTED_SUB', { share: routedShare }) }}
        </span>
      </div>
      <div class="flex flex-col gap-1 p-4 rounded-xl border border-n-weak bg-n-solid-1">
        <span class="text-xs text-n-slate-11">{{ $t('DASHBOARDS.BOT.CLOSED') }}</span>
        <span class="text-2xl font-semibold text-n-teal-11">
          {{ totals.bot_closed_share == null ? '—' : `${totals.bot_closed_share} %` }}
        </span>
        <span class="text-xs text-n-slate-11">{{ $t('DASHBOARDS.BOT.CLOSED_SUB', { count: fmt(totals.bot_closed) }) }}</span>
      </div>
      <div class="flex flex-col gap-1 p-4 rounded-xl border border-n-weak bg-n-solid-1">
        <span class="text-xs text-n-slate-11">{{ $t('DASHBOARDS.BOT.HANDED') }}</span>
        <span class="text-2xl font-semibold text-n-slate-12">{{ fmt(totals.bot_handed_over) }}</span>
        <span class="text-xs text-n-slate-11">
          {{ $t('DASHBOARDS.BOT.HANDED_SUB', { share: totals.bot_handed_over_share ?? '—' }) }}
        </span>
      </div>
      <div class="flex flex-col gap-1 p-4 rounded-xl border border-n-weak bg-n-solid-1">
        <span class="text-xs text-n-slate-11">{{ $t('DASHBOARDS.BOT.AVG') }}</span>
        <span class="text-2xl font-semibold text-n-slate-12">
          {{ fmtAvg(totals.ratings_avg) }}
          <span
            class="text-base font-medium"
            :class="csiTone(totals.csi)"
            :title="$t('DASHBOARDS.CSI_HINT')"
          >
            · CSI {{ fmt(totals.csi) }}
          </span>
        </span>
        <span class="text-xs text-n-slate-11">{{ $t('DASHBOARDS.BOT.AVG_SUB', { count: fmt(totals.ratings_count) }) }}</span>
      </div>
      <div class="flex flex-col gap-1 p-4 rounded-xl border border-n-weak bg-n-solid-1">
        <span class="text-xs text-n-slate-11">{{ $t('DASHBOARDS.BOT.REPLY') }}</span>
        <span class="text-2xl font-semibold text-n-slate-12">{{ fmtSec(totals.bot_reply_median_s) }}</span>
        <span class="text-xs text-n-slate-11">{{ $t('DASHBOARDS.BOT.REPLY_SUB') }}</span>
      </div>
    </div>

    <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <section class="flex flex-col gap-3 p-4 rounded-xl border border-n-weak bg-n-solid-1">
        <h2 class="m-0 text-heading-3 text-n-slate-12">{{ $t('DASHBOARDS.BOT.FUNNEL') }}</h2>
        <div v-for="row in funnel" :key="row.key" class="flex items-center gap-3">
          <span class="w-56 text-sm text-n-slate-12 shrink-0">{{ $t(`DASHBOARDS.BOT.${row.key}`) }}</span>
          <div class="flex-1 h-7 rounded-md bg-n-alpha-1">
            <div
              class="h-7 rounded-md"
              :style="{ width: `${pct(row.value, totals.routed_to_bot)}%`, background: row.color }"
            />
          </div>
          <span class="w-28 text-sm text-right tabular-nums text-n-slate-12 shrink-0">
            <b>{{ fmt(row.value) }}</b>
            <span class="text-n-slate-11"> · {{ pct(row.value, totals.routed_to_bot) }} %</span>
          </span>
        </div>
      </section>

      <section class="flex flex-col gap-3 p-4 rounded-xl border border-n-weak bg-n-solid-1">
        <h2 class="m-0 text-heading-3 text-n-slate-12">{{ $t('DASHBOARDS.BOT.AB') }}</h2>
        <table class="w-full text-sm">
          <thead>
            <tr class="text-xs font-normal text-n-slate-11">
              <th class="pb-1 font-normal text-left" />
              <th class="pb-1 font-normal text-right">{{ $t('DASHBOARDS.BOT.AB_WITH') }}</th>
              <th class="pb-1 font-normal text-right">{{ $t('DASHBOARDS.BOT.AB_WITHOUT') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr class="border-t border-n-weak">
              <td class="py-2 text-n-slate-11">{{ $t('DASHBOARDS.BOT.AB_CONVERSATIONS') }}</td>
              <td class="py-2 text-right tabular-nums text-n-slate-12">{{ fmt(totals.routed_to_bot) }}</td>
              <td class="py-2 text-right tabular-nums text-n-slate-12">{{ fmt(totals.control_group) }}</td>
            </tr>
            <tr class="border-t border-n-weak">
              <td class="py-2 text-n-slate-11">{{ $t('DASHBOARDS.BOT.AB_CLOSED') }}</td>
              <td class="py-2 text-right tabular-nums text-n-slate-12">
                {{ totals.bot_closed_share == null ? '—' : `${totals.bot_closed_share} %` }}
              </td>
              <td class="py-2 text-right tabular-nums text-n-slate-11">—</td>
            </tr>
            <tr class="border-t border-n-weak">
              <td class="py-2 text-n-slate-11">{{ $t('DASHBOARDS.BOT.AB_PLEA') }}</td>
              <td class="py-2 text-right tabular-nums text-n-slate-12">
                {{ pct(totals.bot_escalated_by_client, totals.routed_to_bot) }} %
              </td>
              <td class="py-2 text-right tabular-nums text-n-slate-11">—</td>
            </tr>
            <tr class="border-t border-n-weak text-n-slate-10">
              <td class="py-2">
                {{ $t('DASHBOARDS.BOT.AB_BOOKING') }}
                <span class="block text-xs">{{ $t('DASHBOARDS.BOT.AB_BOOKING_NOTE') }}</span>
              </td>
              <td class="py-2 text-right">—</td>
              <td class="py-2 text-right">—</td>
            </tr>
          </tbody>
        </table>
      </section>
    </div>

    <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <section class="flex flex-col gap-3 p-4 rounded-xl border border-n-weak bg-n-solid-1">
        <h2 class="m-0 text-heading-3 text-n-slate-12">{{ $t('DASHBOARDS.BOT.AVG_BY_DAY') }}</h2>
        <DayChart :points="avgPoints" :color="COLOR.blue" :min="1" :max="5" :format="fmtAvg" />
      </section>
      <section class="flex flex-col gap-3 p-4 rounded-xl border border-n-weak bg-n-solid-1">
        <h2 class="m-0 text-heading-3 text-n-slate-12">{{ $t('DASHBOARDS.BOT.COUNT_BY_DAY') }}</h2>
        <DayChart :points="countPoints" kind="bars" :color="COLOR.teal" />
      </section>
    </div>

    <div class="grid grid-cols-1 gap-4 lg:grid-cols-3">
      <section class="flex flex-col gap-3 p-4 rounded-xl border border-n-weak bg-n-solid-1">
        <h2 class="m-0 text-heading-3 text-n-slate-12">{{ $t('DASHBOARDS.BOT.HIST') }}</h2>
        <div v-for="h in histogram" :key="h.mark" class="flex items-center gap-2 text-sm">
          <span class="w-3 text-n-slate-11 shrink-0">{{ h.mark }}</span>
          <div class="flex-1 h-5 rounded bg-n-alpha-1">
            <div class="h-5 rounded" :style="{ width: `${(h.count * 100) / histMax}%`, background: markColor(h.mark) }" />
          </div>
          <span class="w-8 text-right tabular-nums text-n-slate-11 shrink-0">{{ h.count }}</span>
        </div>
        <span class="text-xs text-n-slate-11">{{ $t('DASHBOARDS.BOT.HIST_NOTE', { count: lowMarks }) }}</span>
      </section>

      <section class="flex flex-col gap-3 p-4 rounded-xl border border-n-weak bg-n-solid-1">
        <h2 class="m-0 text-heading-3 text-n-slate-12">{{ $t('DASHBOARDS.BOT.MODELS') }}</h2>
        <table class="w-full text-sm">
          <thead>
            <tr class="text-xs text-n-slate-11">
              <th class="pb-1 font-normal text-left">{{ $t('DASHBOARDS.BOT.MODEL') }}</th>
              <th class="pb-1 font-normal text-right">{{ $t('DASHBOARDS.BOT.ANSWERS') }}</th>
              <th class="pb-1 font-normal text-right">{{ $t('DASHBOARDS.BOT.RATINGS') }}</th>
              <th class="pb-1 font-normal text-right">{{ $t('DASHBOARDS.BOT.SCORE') }}</th>
              <th class="pb-1 font-normal text-right">CSI</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="m in report.models" :key="m.model" class="border-t border-n-weak">
              <td class="py-2 text-n-slate-12 break-all">{{ m.model }}</td>
              <td class="py-2 text-right tabular-nums text-n-slate-11">{{ fmt(m.answers) }}</td>
              <td class="py-2 text-right tabular-nums text-n-slate-11">{{ fmt(m.ratings_count) }}</td>
              <td class="py-2 text-right tabular-nums text-n-slate-12">{{ fmtAvg(m.ratings_avg) }}</td>
              <td class="py-2 text-right tabular-nums" :class="csiTone(m.csi)">
                {{ fmt(m.csi) }}
              </td>
            </tr>
          </tbody>
        </table>
      </section>

      <section class="flex flex-col gap-3 p-4 rounded-xl border border-n-weak bg-n-solid-1">
        <h2 class="m-0 text-heading-3 text-n-slate-12">{{ $t('DASHBOARDS.BOT.REASONS') }}</h2>
        <p v-if="!reasons.length" class="text-sm text-n-slate-11">{{ $t('DASHBOARDS.BOT.NO_REASONS') }}</p>
        <div v-for="[reason, count] in reasons" :key="reason" class="flex flex-col gap-1 py-1 text-sm">
          <div class="flex justify-between gap-3">
            <span class="text-n-slate-12">{{ reason }}</span>
            <span class="tabular-nums text-n-slate-11 shrink-0">{{ count }} · {{ pct(count, reasonsTotal) }} %</span>
          </div>
          <div class="h-2 rounded-full bg-n-alpha-1">
            <div class="h-2 rounded-full" :style="{ width: `${pct(count, reasonsTotal)}%`, background: COLOR.amber }" />
          </div>
        </div>
      </section>
    </div>
  </div>
</template>
